import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CreditCounter } from './CreditComponents';
import { jobTrackerAPI, freeToolsAPI } from '../services/api';
import { trackAuditStarted, trackAuditCompleted, trackAuditPrinted } from '../lib/analytics';
import logo from '../assets/logo.png';

const CREDIT_COST = 5;

const FutureProofAudit = () => {
  const { isAuthenticated, user, refreshUser } = useAuth();
  const [resumeText, setResumeText] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [auditResult, setAuditResult] = useState(null);
  const [error, setError] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userCredits, setUserCredits] = useState(0);
  const [hasUnlimited, setHasUnlimited] = useState(false);
  const [creditsLoading, setCreditsLoading] = useState(true);

  // Fetch credits directly from API (same as CreditCounter)
  useEffect(() => {
    const fetchCredits = async () => {
      if (!isAuthenticated) {
        setCreditsLoading(false);
        return;
      }
      try {
        const data = await freeToolsAPI.getCredits();
        setUserCredits(data.credits || 0);
        setHasUnlimited(Boolean(data.has_unlimited));
      } catch (error) {
        console.error('Failed to fetch credits:', error);
        setUserCredits(0);
        setHasUnlimited(false);
      } finally {
        setCreditsLoading(false);
      }
    };

    fetchCredits();
  }, [isAuthenticated]);

  const hasEnoughCredits = hasUnlimited || userCredits >= CREDIT_COST;

  const handleRunAudit = async () => {
    if (!resumeText.trim()) {
      setError('Please paste your resume or work history');
      return;
    }

    if (resumeText.trim().length < 200) {
      setError('Please provide more detail about your work history (at least 200 characters)');
      return;
    }

    if (!hasEnoughCredits) {
      setError(`You need ${CREDIT_COST} credits to run the Future-Proof Audit. You have ${userCredits}.`);
      return;
    }

    setIsAnalyzing(true);
    setError(null);
    setAuditResult(null);
    trackAuditStarted();

    try {
      const response = await jobTrackerAPI.runFutureProofAudit(resumeText);
      setAuditResult(response);
      trackAuditCompleted();
      // Refresh credits after successful audit
      const data = await freeToolsAPI.getCredits();
      setUserCredits(data.credits || 0);
      setHasUnlimited(Boolean(data.has_unlimited));
      // Dispatch event so CreditCounter updates too
      window.dispatchEvent(new Event('credit_update'));
    } catch (err) {
      console.error('Audit error:', err);
      setError(err.response?.data?.detail || 'Failed to run audit. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleClear = () => {
    setResumeText('');
    setAuditResult(null);
    setError(null);
  };

  const handlePrint = () => {
    trackAuditPrinted();
    window.print();
  };

  const reportDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Future-Proof Audit</h2>
          <p className="text-gray-600 mb-6">Sign in to discover your career pivot possibilities</p>
          <Link
            to="/tracker"
            className="inline-block px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all"
          >
            Sign In to Get Started
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50">
      {/* Print-specific styles */}
      <style>{`
        @media print {
          /* Page margins control where browser headers/footers appear.
             We can't directly hide them (browsers don't allow that for security),
             but our own print-only header at the top of the document plus
             generous top margin pushes any browser-injected text to a separate
             header region the user can hide via "More settings → Headers and footers". */
          @page {
            size: auto;
            margin: 0.5in 0.5in 0.5in 0.5in;
          }
          html, body {
            background: white !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          .print-break-inside-avoid { break-inside: avoid; page-break-inside: avoid; }
          .print-break-before { break-before: page; page-break-before: always; }
          /* Force print colors on gradient/colored sections */
          .print-force-colors {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          /* Hide any sticky nav remnants */
          nav, .sticky { position: static !important; }
        }
        .print-only { display: none; }
      `}</style>

      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100 no-print">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-3">
              <img src={logo} alt="AlignifyNOW" className="h-12 sm:h-16 w-auto" />
              <span className="text-base sm:text-lg font-bold tracking-tight text-slate-900 hidden sm:inline">
                Alignify<span className="text-blue-600">NOW</span>
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-6">
              <Link to="/" className="text-sm text-gray-600 hover:text-gray-900">Tool Hub</Link>
              <Link to="/tracker" className="text-sm text-gray-600 hover:text-gray-900">Job Tracker</Link>
              <Link to="/pricing" className="text-sm text-gray-600 hover:text-gray-900">Pricing</Link>
              {user?.role === 'admin' && (
                <Link
                  to="/admin"
                  data-testid="nav-admin-link-desktop"
                  className="text-sm text-purple-700 hover:text-purple-900 font-medium"
                >
                  Admin
                </Link>
              )}
              <CreditCounter />
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-gray-600 hover:text-gray-900"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-100">
              <div className="flex flex-col space-y-3">
                <Link to="/" className="text-gray-600 hover:text-gray-900 py-2">Tool Hub</Link>
                <Link to="/tracker" className="text-gray-600 hover:text-gray-900 py-2">Job Tracker</Link>
                <Link to="/pricing" className="text-gray-600 hover:text-gray-900 py-2">Pricing</Link>
                {user?.role === 'admin' && (
                  <Link
                    to="/admin"
                    data-testid="nav-admin-link-mobile"
                    className="text-purple-700 hover:text-purple-900 py-2 font-medium"
                  >
                    🛡️ Admin
                  </Link>
                )}
                <div className="pt-2"><CreditCounter /></div>
              </div>
            </div>
          )}
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
        {/* Print-only report header */}
        <div className="print-only mb-8">
          <div className="flex items-center justify-between border-b-2 border-gray-900 pb-4">
            <div className="flex items-center gap-3">
              <img src={logo} alt="AlignifyNOW" className="h-12 w-auto" />
              <div>
                <div className="text-lg font-bold text-slate-900">
                  Alignify<span className="text-blue-600">NOW</span>
                </div>
                <div className="text-xs text-gray-600">Future-Proof Audit Report</div>
              </div>
            </div>
            <div className="text-right text-xs text-gray-600">
              <div>Prepared for: <span className="font-semibold text-gray-900">{user?.email || ''}</span></div>
              <div>Date: <span className="font-semibold text-gray-900">{reportDate}</span></div>
            </div>
          </div>
        </div>

        {/* Header */}
        <div className="text-center mb-8 sm:mb-12 no-print">
          <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-indigo-100 to-purple-100 text-indigo-700 rounded-full text-sm font-medium mb-4">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            Career Evolution Tool
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Future-Proof Audit
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
            Discover your transferable strengths and explore exciting new career paths. 
            The future is full of possibilities — let&apos;s map yours.
          </p>
        </div>

        {!auditResult ? (
          /* Input Section */
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sm:p-8">
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Your Work History
              </label>
              <p className="text-sm text-gray-500 mb-4">
                Paste your complete resume or describe your work experience. The more detail you provide, 
                the better we can identify your transferable skills and suggest exciting pivot opportunities.
              </p>
              <textarea
                value={resumeText}
                onChange={(e) => setResumeText(e.target.value)}
                placeholder="Paste your resume here, or describe your work experience including:

• Job titles and companies
• Key responsibilities and achievements
• Skills you've developed
• Projects you've led or contributed to
• Any certifications or education

The more detail, the better your audit results..."
                className="w-full h-64 sm:h-80 p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none text-gray-700"
              />
              <div className="flex justify-between items-center mt-2 text-sm text-gray-500">
                <span>{resumeText.length} characters</span>
                <span>Minimum 200 characters</span>
              </div>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                {error}
              </div>
            )}

            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2 text-sm flex-wrap">
                <span className="text-gray-600">Cost:</span>
                <span className="text-gray-400 line-through" data-testid="audit-cost-original">8 Credits</span>
                <span className="font-semibold text-indigo-600" data-testid="audit-cost-current">{CREDIT_COST} Credits</span>
                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-bold rounded-full">SAVE 3</span>
                <span className="text-gray-400">|</span>
                {hasUnlimited ? (
                  <>
                    <span className="text-gray-600">Your plan:</span>
                    <span className="font-semibold text-green-600">All-Access (unlimited)</span>
                  </>
                ) : (
                  <>
                    <span className="text-gray-600">You have:</span>
                    <span className={`font-semibold ${hasEnoughCredits ? 'text-green-600' : 'text-red-600'}`}>
                      {userCredits} Credits
                    </span>
                  </>
                )}
              </div>

              <button
                onClick={handleRunAudit}
                disabled={isAnalyzing || !hasEnoughCredits}
                className={`w-full sm:w-auto px-8 py-3 rounded-xl font-semibold transition-all ${
                  isAnalyzing || !hasEnoughCredits
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700 shadow-lg hover:shadow-xl'
                }`}
              >
                {isAnalyzing ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Analyzing Your Future...
                  </span>
                ) : (
                  'Run Future-Proof Audit'
                )}
              </button>
            </div>

            {!hasEnoughCredits && !hasUnlimited && (
              <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <p className="text-amber-800 text-sm">
                  You need {CREDIT_COST - userCredits} more credits.{' '}
                  <Link to="/pricing" className="font-semibold underline">Get more credits</Link>
                </p>
              </div>
            )}

            {/* Privacy Note */}
            <div className="mt-6 flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
              <svg className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <div className="text-sm text-gray-600">
                <strong className="text-gray-700">Your data stays yours.</strong> We use zero-retention processing — 
                your resume is analyzed and immediately discarded. Results are shown once; refresh the page and they&apos;re gone.
              </div>
            </div>
          </div>
        ) : (
          /* Results Section */
          <div className="space-y-6">
            {/* Suggested Pivot Paths */}
            {auditResult.pivot_paths && auditResult.pivot_paths.length > 0 && (
              <div className="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-2xl shadow-lg p-6 sm:p-8 text-white print-break-inside-avoid print-force-colors">
                <h2 className="text-xl sm:text-2xl font-bold mb-4 flex items-center">
                  <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                  </svg>
                  Your Pivot Possibilities
                </h2>
                <p className="text-indigo-100 mb-6">
                  Based on your experience, here are exciting career paths where your skills can shine:
                </p>
                <div className="grid gap-4">
                  {auditResult.pivot_paths.map((path, index) => (
                    <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                      <h3 className="font-bold text-lg mb-2">{path.role}</h3>
                      <p className="text-indigo-100 text-sm">{path.rationale}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* The Bridge - Transferable Skills */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sm:p-8 print-break-inside-avoid">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 flex items-center">
                <span className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center mr-3">
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </span>
                The Bridge — Your Transferable Strengths
              </h2>
              <p className="text-gray-600 mb-6 ml-13">
                These are the skills and experiences that will carry you forward into new opportunities.
              </p>

              {auditResult.bridge && (
                <div className="space-y-4 ml-0 sm:ml-13">
                  {auditResult.bridge.soft_skills && (
                    <div>
                      <h4 className="font-semibold text-gray-700 mb-2">Soft Skills</h4>
                      <div className="flex flex-wrap gap-2">
                        {auditResult.bridge.soft_skills.map((skill, i) => (
                          <span key={i} className="px-3 py-1 bg-green-50 text-green-700 rounded-full text-sm font-medium">
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {auditResult.bridge.hard_skills && (
                    <div>
                      <h4 className="font-semibold text-gray-700 mb-2">Hard Skills</h4>
                      <div className="flex flex-wrap gap-2">
                        {auditResult.bridge.hard_skills.map((skill, i) => (
                          <span key={i} className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm font-medium">
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {auditResult.bridge.explanation && (
                    <p className="text-gray-600 mt-4 p-4 bg-gray-50 rounded-xl">
                      {auditResult.bridge.explanation}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* The Gap - Areas to Develop */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sm:p-8 print-break-inside-avoid">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 flex items-center">
                <span className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center mr-3">
                  <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                  </svg>
                </span>
                The Gap — Growth Opportunities
              </h2>
              <p className="text-gray-600 mb-6 ml-13">
                These are areas where building new skills will unlock your pivot potential.
              </p>

              {auditResult.gap && (
                <div className="space-y-4 ml-0 sm:ml-13">
                  {auditResult.gap.hard_skills && auditResult.gap.hard_skills.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-gray-700 mb-2">Skills to Develop</h4>
                      <div className="flex flex-wrap gap-2">
                        {auditResult.gap.hard_skills.map((skill, i) => (
                          <span key={i} className="px-3 py-1 bg-amber-50 text-amber-700 rounded-full text-sm font-medium">
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {auditResult.gap.software && auditResult.gap.software.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-gray-700 mb-2">Software to Learn</h4>
                      <div className="flex flex-wrap gap-2">
                        {auditResult.gap.software.map((sw, i) => (
                          <span key={i} className="px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-sm font-medium">
                            {sw}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {auditResult.gap.technical_knowledge && auditResult.gap.technical_knowledge.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-gray-700 mb-2">Knowledge Areas</h4>
                      <div className="flex flex-wrap gap-2">
                        {auditResult.gap.technical_knowledge.map((tk, i) => (
                          <span key={i} className="px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full text-sm font-medium">
                            {tk}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* The Action Plan */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sm:p-8 print-break-inside-avoid">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 flex items-center">
                <span className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center mr-3">
                  <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                </span>
                The Action Plan — Your Next Steps
              </h2>
              <p className="text-gray-600 mb-6 ml-13">
                Concrete, actionable steps to start your career evolution today.
              </p>

              {auditResult.action_plan && auditResult.action_plan.length > 0 && (
                <div className="space-y-4 ml-0 sm:ml-13">
                  {auditResult.action_plan.map((step, index) => (
                    <div key={index} className="flex items-start gap-4 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl">
                      <span className="w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                        {index + 1}
                      </span>
                      <div>
                        <h4 className="font-semibold text-gray-900">{step.title}</h4>
                        <p className="text-gray-600 text-sm mt-1">{step.description}</p>
                        {step.resources && (
                          <p className="text-indigo-600 text-sm mt-2 font-medium">{step.resources}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center no-print">
              <button
                onClick={handlePrint}
                data-testid="print-audit-report-btn"
                className="px-6 py-3 bg-white border-2 border-indigo-600 text-indigo-600 font-semibold rounded-xl hover:bg-indigo-50 transition-colors flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                Print / Save as PDF
              </button>
              <button
                onClick={handleClear}
                data-testid="start-new-audit-btn"
                className="px-6 py-3 bg-white border-2 border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors"
              >
                Start New Audit
              </button>
              <Link
                to="/tracker"
                data-testid="back-to-tracker-btn"
                className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all text-center"
              >
                Back to Job Tracker
              </Link>
            </div>

            {/* Privacy + Save Note */}
            <div className="bg-white border-2 border-indigo-200 rounded-2xl p-6 sm:p-8 space-y-5 no-print" data-testid="audit-privacy-note">
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <span aria-hidden="true">🔒</span> Your Privacy Comes First
                </h3>
                <p className="text-gray-700 text-sm sm:text-base">
                  We never store your résumé or audit results.
                </p>
                <p className="text-gray-700 text-sm sm:text-base">
                  To protect your data, your Future-Proof Audit is generated once and then deleted from our system.
                </p>
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <span aria-hidden="true">📄</span> Save or Print Your Report
                </h3>
                <p className="text-gray-700 text-sm sm:text-base">
                  If you&apos;d like to keep a copy, please download or print it now.
                </p>
                <p className="text-gray-700 text-sm sm:text-base">
                  Because we don&apos;t retain your information, you&apos;ll need to re-run the audit if you leave this page
                  {!hasUnlimited && <> — and each re-run will cost another <strong>{CREDIT_COST} credits</strong></>}.
                </p>
              </div>
            </div>

            {/* Print-only footer */}
            <div className="print-only mt-8 pt-4 border-t border-gray-300 text-center text-xs text-gray-500 space-y-2">
              <div className="font-semibold text-gray-700">
                Ready for the next step? Track your applications for these roles in your AlignifyNOW Job Tracker → alignifynow.com/tracker
              </div>
              <div>
                Generated by AlignifyNOW &middot; alignifynow.com &middot; This report is confidential and prepared exclusively for {user?.email || 'you'}.
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 py-8 mt-12 no-print">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-gray-400 text-sm">
            © 2026 AlignifyNOW. All rights reserved. Built by Matambanadzo Studio.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default FutureProofAudit;
